import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
} from "../../services/UserService";
import { User } from "../../types/user.type";

interface CurrentUserState {
  name: string;
  email: string;
}

interface UserManagementState {
  currentUser: CurrentUserState;
  userList: User[];
  selectedUser: User | null;
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const storedUser = localStorage.getItem("user");
const parsedStoredUser = storedUser ? JSON.parse(storedUser) : null;

const initialState: UserManagementState = {
  currentUser: {
    name: parsedStoredUser?.name || "",
    email: parsedStoredUser?.email || "",
  },
  userList: [],
  selectedUser: null,
  totalCount: 0,
  loading: false,
  error: null
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // Keep existing actions for backward compatibility
    setUser(state, action: PayloadAction<CurrentUserState>) {
      state.currentUser = action.payload;
    },
    clearUser(state) {
      state.currentUser = initialState.currentUser;
    },

    // Add new actions for user management
    clearSelectedUser(state) {
      state.selectedUser = null;
    },
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all users
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.userList = action.payload.users || [];
          state.totalCount = action.payload.totalCount || 0;
        } else {
          state.userList = [];
          state.totalCount = 0;
        }
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      })

      // Get user by ID
      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload || null;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch user";
      })

      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.userList = [...state.userList, action.payload];
          state.totalCount += 1;
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create user";
      })

      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload) {
          // Update in the list
          state.userList = state.userList.map(user => {
            return user.id === payload.id ? payload : user;
          });

          // Update selected user if it matches
          if (state.selectedUser?.id === payload.id) {
            state.selectedUser = payload;
          }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update user";
      })

      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        const userId = action.payload;
        if (userId !== undefined) {
          // Remove from list
          state.userList = state.userList.filter(user => user.id !== userId);
          state.totalCount = Math.max(0, state.totalCount - 1);

          // Clear selected user if it matches
          if (state.selectedUser?.id === userId) {
            state.selectedUser = null;
          }
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete user";
      });
  },
});

export const { setUser, clearUser, clearSelectedUser, clearError } = userSlice.actions;
export default userSlice.reducer;